import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, X, PencilSimple, Trash, Phone, MapPin, Warning, HardHat, User } from '@phosphor-icons/react';
import api from '@/api/axios';
import { useAuth } from '@/contexts/AuthContext';

const MODALIDADES = [
  { id: 'planilla', label: 'Planilla' },
  { id: 'contratista', label: 'Contratista' },
];
const ROLES = [
  { id: 'instalador', label: 'Instalador' },
  { id: 'ayudante', label: 'Ayudante' },
];

export default function Instaladores() {
  useOutletContext();
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const isAdmin = role === 'admin' || role === 'gerente' || role === 'supervisor';
  const canAccess = ['admin', 'supervisor', 'gerente', 'produccion', 'operaciones'].includes(role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterActivo, setFilterActivo] = useState('true');
  const [filterEsp, setFilterEsp] = useState('all');
  const [q, setQ] = useState('');
  const [especialidades, setEspecialidades] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showEspManager, setShowEspManager] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterActivo !== 'all') params.activo = filterActivo === 'true';
      if (filterEsp !== 'all') params.especialidad = filterEsp;
      if (q.trim()) params.q = q.trim();
      const res = await api.get('/instaladores', { params });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error('Error cargando instaladores');
    } finally {
      setLoading(false);
    }
  };

  const loadEsp = async () => {
    try {
      const r = await api.get('/instaladores/especialidades/list');
      setEspecialidades(r.data?.items || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterActivo, filterEsp, q]);
  useEffect(() => { loadEsp(); }, []);

  const openCreate = () => { setEditing(null); setShowForm(true); };
  const openEdit = (row) => { setEditing(row); setShowForm(true); };

  const handleDelete = async (row) => {
    if (!window.confirm(`¿Marcar "${row.nombre}" como inactivo?\n(Se conserva el historial en programaciones)`)) return;
    try {
      await api.delete(`/instaladores/${row.id}`);
      toast.success('Instalador desactivado');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al eliminar');
    }
  };

  const activos = useMemo(() => items.filter(i => i.activo), [items]);
  const stats = useMemo(() => ({
    total: items.length,
    activos: activos.length,
    inactivos: items.length - activos.length,
    planilla: activos.filter(i => i.modalidad === 'planilla').length,
    contratista: activos.filter(i => i.modalidad === 'contratista').length,
  }), [items, activos]);

  if (!canAccess) {
    return (
      <div className="p-10 text-center text-sm text-[var(--text-muted)]" data-testid="instaladores-no-access">
        No tienes acceso al módulo de Instaladores. Pide a un admin que te lo habilite si lo necesitas.
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-4" data-testid="instaladores-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-[var(--text)] heading-font flex items-center gap-2">
            <HardHat size={26} weight="fill" /> Instaladores y Ayudantes
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-0.5">Catálogo maestro de personal de instalación. Usado desde el módulo Programación.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <button onClick={() => setShowEspManager(true)} className="px-3 py-1.5 text-xs font-bold text-[var(--text-muted)] hover:text-[var(--text)] border border-[var(--border)] rounded-md" data-testid="btn-esp-manager">
              🏷️ Especialidades
            </button>
          )}
          {isAdmin && (
            <button onClick={openCreate} className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1" data-testid="btn-new-instalador">
              <Plus size={14} weight="bold" /> Nuevo instalador
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[
          { label: 'Total', value: stats.total, color: 'text-[var(--text)]' },
          { label: 'Activos', value: stats.activos, color: 'text-emerald-500' },
          { label: 'Inactivos', value: stats.inactivos, color: 'text-red-500' },
          { label: 'Planilla', value: stats.planilla, color: 'text-blue-500' },
          { label: 'Contratistas', value: stats.contratista, color: 'text-amber-500' },
        ].map(s => (
          <div key={s.label} className="bg-[var(--surface)] border border-[var(--border)] rounded-md p-2.5">
            <p className="text-[10px] uppercase text-[var(--text-muted)] font-bold">{s.label}</p>
            <p className={`text-xl font-black ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-md p-3 grid grid-cols-1 sm:grid-cols-4 gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔎 Buscar por nombre…" className="bg-[var(--bg)] border border-[var(--border)] rounded px-3 py-1.5 text-sm text-[var(--text)]" data-testid="inst-search" />
        <select value={filterActivo} onChange={(e) => setFilterActivo(e.target.value)} className="bg-[var(--bg)] border border-[var(--border)] rounded px-3 py-1.5 text-sm text-[var(--text)]" data-testid="inst-filter-activo">
          <option value="true">Solo activos</option>
          <option value="false">Solo inactivos</option>
          <option value="all">Todos</option>
        </select>
        <select value={filterEsp} onChange={(e) => setFilterEsp(e.target.value)} className="bg-[var(--bg)] border border-[var(--border)] rounded px-3 py-1.5 text-sm text-[var(--text)]" data-testid="inst-filter-esp">
          <option value="all">Todas las especialidades</option>
          {especialidades.map(e => <option key={e.id} value={e.nombre}>{e.nombre}</option>)}
        </select>
      </div>

      {/* Tabla */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-md overflow-x-auto">
        <table className="w-full text-xs min-w-[900px]">
          <thead className="bg-[var(--surface-muted)]">
            <tr className="text-[10px] uppercase font-bold text-[var(--text-muted)] border-b border-[var(--border)]">
              <th className="text-left py-2 px-3">Nombre</th>
              <th className="text-left py-2 px-3">Rol</th>
              <th className="text-left py-2 px-3">Modalidad</th>
              <th className="text-left py-2 px-3">Especialidades</th>
              <th className="text-left py-2 px-3">Teléfono</th>
              <th className="text-left py-2 px-3">Dirección</th>
              <th className="text-left py-2 px-3">Emergencia</th>
              <th className="text-center py-2 px-3">Estado</th>
              <th className="text-right py-2 px-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="py-8 text-center text-[var(--text-muted)]">Cargando…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={9} className="py-8 text-center text-[var(--text-muted)] italic">
                Aún no hay instaladores. Crea el primero con &quot;Nuevo instalador&quot;.
              </td></tr>
            ) : items.map(row => (
              <tr key={row.id} className={`border-b border-[var(--border)]/40 hover:bg-[var(--surface-muted)] ${!row.activo ? 'opacity-50' : ''}`} data-testid={`inst-row-${row.id}`}>
                <td className="py-2 px-3">
                  <p className="font-bold text-[var(--text)]">{row.nombre}</p>
                  {row.notas && <p className="text-[10px] text-[var(--text-muted)] truncate max-w-[220px]">📝 {row.notas}</p>}
                </td>
                <td className="py-2 px-3">
                  {row.rol === 'instalador' ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-300 font-bold">👷 Instalador</span>
                    : row.rol === 'ayudante' ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-600 dark:text-purple-300 font-bold">🤝 Ayudante</span>
                    : <span className="text-[var(--text-faint)]">—</span>}
                </td>
                <td className="py-2 px-3">
                  {row.modalidad === 'planilla' ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 font-bold">📋 Planilla</span>
                    : row.modalidad === 'contratista' ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-300 font-bold">📄 Contratista</span>
                    : <span className="text-[var(--text-faint)]">—</span>}
                </td>
                <td className="py-2 px-3">
                  <div className="flex flex-wrap gap-1">
                    {(row.especialidades || []).map(e => (
                      <span key={e} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--surface-muted)] border border-[var(--border)] text-[var(--text-muted)]">{e}</span>
                    ))}
                    {(!row.especialidades || row.especialidades.length === 0) && <span className="text-[var(--text-faint)] text-xs">—</span>}
                  </div>
                </td>
                <td className="py-2 px-3 text-[var(--text)]">{row.telefono || '—'}</td>
                <td className="py-2 px-3 text-[var(--text-muted)]">{row.direccion || '—'}</td>
                <td className="py-2 px-3 text-[var(--text-muted)]">
                  {row.contacto_emergencia_nombre ? <>{row.contacto_emergencia_nombre}<br /><span className="text-[10px]">{row.contacto_emergencia_telefono || ''}</span></> : '—'}
                </td>
                <td className="py-2 px-3 text-center">
                  {row.activo ? <span className="text-[10px] font-bold text-emerald-500">● Activo</span>
                    : <span className="text-[10px] font-bold text-[var(--text-faint)]">○ Inactivo</span>}
                </td>
                <td className="py-2 px-3 text-right">
                  {isAdmin && <>
                    <button onClick={() => openEdit(row)} className="p-1 text-blue-500 hover:bg-blue-500/10 rounded" title="Editar" data-testid={`inst-edit-${row.id}`}><PencilSimple size={14} /></button>
                    <button onClick={() => handleDelete(row)} className="p-1 text-red-500 hover:bg-red-500/10 rounded ml-1" title="Desactivar" data-testid={`inst-del-${row.id}`}><Trash size={14} /></button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <InstaladorFormModal
          instalador={editing}
          especialidades={especialidades}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      {showEspManager && (
        <EspecialidadesManager
          items={especialidades}
          isAdmin={isAdmin}
          onClose={() => setShowEspManager(false)}
          onChanged={loadEsp}
        />
      )}
    </div>
  );
}

const InstaladorFormModal = ({ instalador, especialidades, onClose, onSaved }) => {
  const isEdit = !!instalador;
  const [f, setF] = useState({
    nombre: instalador?.nombre || '',
    telefono: instalador?.telefono || '',
    direccion: instalador?.direccion || '',
    contacto_emergencia_nombre: instalador?.contacto_emergencia_nombre || '',
    contacto_emergencia_telefono: instalador?.contacto_emergencia_telefono || '',
    especialidades: instalador?.especialidades || [],
    modalidad: instalador?.modalidad || 'contratista',
    rol: instalador?.rol || 'instalador',
    activo: instalador?.activo !== false,
    notas: instalador?.notas || '',
  });
  const [saving, setSaving] = useState(false);

  const toggleEsp = (name) => {
    setF(prev => ({
      ...prev,
      especialidades: prev.especialidades.includes(name) ? prev.especialidades.filter(x => x !== name) : [...prev.especialidades, name],
    }));
  };

  const save = async () => {
    if (!f.nombre.trim()) { toast.error('Nombre requerido'); return; }
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/instaladores/${instalador.id}`, f);
      else await api.post('/instaladores', f);
      toast.success(isEdit ? 'Instalador actualizado' : 'Instalador creado');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const inp = 'w-full bg-[var(--bg)] border border-[var(--border)] rounded px-2 py-1.5 text-sm text-[var(--text)]';
  const lbl = 'block text-[10px] uppercase font-bold text-[var(--text-muted)] mb-1';

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="modal-instalador-form">
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-black text-[var(--text)]">{isEdit ? '✏️ Editar' : '➕ Nuevo'} Instalador</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className={lbl}>Nombre completo *</label>
            <input value={f.nombre} onChange={(e) => setF({...f, nombre: e.target.value})} className={inp} data-testid="inst-form-nombre" />
          </div>
          <div>
            <label className={lbl}>Teléfono (WhatsApp)</label>
            <input value={f.telefono} onChange={(e) => setF({...f, telefono: e.target.value})} placeholder="502xxxxxxxx" className={inp} data-testid="inst-form-tel" />
          </div>
          <div>
            <label className={lbl}>Dirección</label>
            <input value={f.direccion} onChange={(e) => setF({...f, direccion: e.target.value})} className={inp} />
          </div>
          <div>
            <label className={lbl}>Rol</label>
            <select value={f.rol} onChange={(e) => setF({...f, rol: e.target.value})} className={inp} data-testid="inst-form-rol">
              {ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Modalidad</label>
            <select value={f.modalidad} onChange={(e) => setF({...f, modalidad: e.target.value})} className={inp} data-testid="inst-form-modalidad">
              {MODALIDADES.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className={lbl}>Especialidades (multi-select)</label>
            <div className="flex flex-wrap gap-1.5">
              {especialidades.map(e => (
                <button key={e.id} type="button" onClick={() => toggleEsp(e.nombre)}
                        className={`text-xs px-2 py-1 rounded-full border font-bold ${f.especialidades.includes(e.nombre)
                          ? 'bg-purple-600 border-purple-700 text-white'
                          : 'bg-[var(--bg)] border-[var(--border)] text-[var(--text-muted)] hover:border-purple-500/50'}`}
                        data-testid={`inst-esp-toggle-${e.nombre}`}>
                  {e.nombre}
                </button>
              ))}
              {especialidades.length === 0 && <span className="text-[var(--text-muted)] text-xs italic">Sin especialidades. Créalas desde el botón 🏷️ Especialidades.</span>}
            </div>
          </div>
          <div>
            <label className={lbl}>Contacto emergencia — nombre</label>
            <input value={f.contacto_emergencia_nombre} onChange={(e) => setF({...f, contacto_emergencia_nombre: e.target.value})} className={inp} />
          </div>
          <div>
            <label className={lbl}>Contacto emergencia — teléfono</label>
            <input value={f.contacto_emergencia_telefono} onChange={(e) => setF({...f, contacto_emergencia_telefono: e.target.value})} className={inp} />
          </div>
          <div className="col-span-2">
            <label className={lbl}>Notas / observaciones</label>
            <textarea value={f.notas} onChange={(e) => setF({...f, notas: e.target.value})} rows={2} className={inp} />
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <input type="checkbox" id="activo-chk" checked={f.activo} onChange={(e) => setF({...f, activo: e.target.checked})} />
            <label htmlFor="activo-chk" className="text-xs text-[var(--text)]">Activo (disponible para asignar en programaciones)</label>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-[var(--border)]">
          <button onClick={onClose} disabled={saving} className="px-3 py-1.5 text-xs font-bold text-[var(--text-muted)] hover:text-[var(--text)]">Cancelar</button>
          <button onClick={save} disabled={saving} className="px-4 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded text-xs font-bold" data-testid="inst-form-save">
            {saving ? 'Guardando…' : (isEdit ? '💾 Guardar cambios' : '➕ Crear instalador')}
          </button>
        </div>
      </div>
    </div>
  );
};

const EspecialidadesManager = ({ items, isAdmin, onClose, onChanged }) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const add = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post('/instaladores/especialidades', { nombre: name.trim() });
      toast.success('Especialidad agregada');
      setName('');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('¿Eliminar esta especialidad? Los instaladores que la tengan la seguirán mostrando en su historial.')) return;
    try {
      await api.delete(`/instaladores/especialidades/${id}`);
      toast.success('Especialidad eliminada');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="modal-especialidades">
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-black text-[var(--text)]">🏷️ Especialidades</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        {isAdmin && (
          <div className="flex gap-2 mb-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nueva especialidad…" className="flex-1 bg-[var(--bg)] border border-[var(--border)] rounded px-3 py-1.5 text-sm text-[var(--text)]" data-testid="esp-new-input" />
            <button onClick={add} disabled={saving || !name.trim()} className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded text-xs font-bold disabled:bg-gray-400" data-testid="esp-add-btn">Agregar</button>
          </div>
        )}
        <div className="space-y-1 max-h-72 overflow-y-auto">
          {items.length === 0 ? <p className="text-xs text-[var(--text-muted)] italic py-4 text-center">Sin especialidades.</p> :
            items.map(e => (
              <div key={e.id} className="flex items-center justify-between bg-[var(--bg)] border border-[var(--border)] rounded px-3 py-1.5" data-testid={`esp-item-${e.nombre}`}>
                <span className="text-sm text-[var(--text)]">{e.nombre}</span>
                {isAdmin && <button onClick={() => remove(e.id)} className="text-red-500 hover:bg-red-500/10 rounded p-1"><Trash size={12} /></button>}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
